import { Typography } from "@eventbrite/marmalade"
import { FC, ReactNode, useEffect, useState } from "react"
import { Button as MarmaladeButton } from "@eventbrite/marmalade"
import classNames from "classnames"

// utils
import { getBaseUrl } from "../../../../utils/general_utils"
import {
  customRendererToRemovePTag,
  renderRichText
} from "../../../../utils/contentful/render_rich_text"

// types
import { EventbriteOrder } from "../../../../utils/auth/enrich_user_data"

// styles
import * as MyTicketsStyles from "./MyTickets.module.scss"

import placeholderSvgImg from "../../../../../static/images/event_placeholder_image.svg"
import { useSelector } from "react-redux"
import { RootState } from "../../../../store/store"

type TrimmedSingleOrder = {
  title: string
  imageUrl: string
  displayDate: string
  ticketPageUrl: string
}

type ContentfulContent =
  | {
      noTicketsFoundContent: {
        eventActionComplexHeader: ReactNode
        eventActionComplexPrimaryCta: ReactNode
        eventActionComplexSecondaryCta: ReactNode
        eventActionComplexTertiaryCta: ReactNode
      }
      ticketsFoundActionComplex: {
        eventActionComplexHeader: ReactNode
        eventActionComplexPrimaryCta: ReactNode
        eventActionComplexSecondaryCta: ReactNode
        eventActionComplexTertiaryCta: ReactNode
      }
      messageNoUpcomingOrders: ReactNode
    }
  | Record<string, never>

export type Props = {
  noTicketsFoundActionComplex: Queries.HomepageDataQuery["noTicketsFoundActionComplex"]
  ticketsFoundActionComplex: Queries.HomepageDataQuery["ticketsFoundActionComplex"]
  messageNoUpcomingOrders: Queries.HomepageDataQuery["messageNoUpcomingOrders"]
  orders?: EventbriteOrder[]
  columnLayout?: boolean
}

export const MyTickets: FC<Props> = ({
  columnLayout,
  messageNoUpcomingOrders,
  noTicketsFoundActionComplex,
  orders,
  ticketsFoundActionComplex
}) => {
  const [trimmedOrders, setTrimmedOrders] = useState<TrimmedSingleOrder[]>([])
  const activeOrder = trimmedOrders.length > 0 ? trimmedOrders[0] : null
  const [contentfulContent, setContentfulContent] = useState<ContentfulContent>(
    {}
  )
  const hasAttemptedLogin = useSelector(
    (state: RootState) => state.metaData.hasAttemptedLogin
  )

  useEffect(() => {
    const x: ContentfulContent = Object.assign(contentfulContent)
    if (process.env.GATSBY_DEBUG === "true") {
      console.log("🚀 ~ useEffect ~ x:", x)
    }

    if (noTicketsFoundActionComplex) {
      x.noTicketsFoundContent = {
        eventActionComplexHeader:
          noTicketsFoundActionComplex.eventActionComplexHeader,
        eventActionComplexPrimaryCta: renderRichText(
          noTicketsFoundActionComplex?.eventActionComplexPrimaryCta
            ?.microcopyValue?.raw ?? "",
          customRendererToRemovePTag
        ),
        eventActionComplexSecondaryCta: renderRichText(
          noTicketsFoundActionComplex?.eventActionComplexSecondaryCta
            ?.microcopyValue?.raw ?? "",
          customRendererToRemovePTag
        ),
        eventActionComplexTertiaryCta: renderRichText(
          noTicketsFoundActionComplex?.eventActionComplexTertiaryCta
            ?.microcopyValue?.raw ?? "",
          customRendererToRemovePTag
        )
      }
    }

    if (ticketsFoundActionComplex) {
      x.ticketsFoundActionComplex = {
        eventActionComplexHeader:
          ticketsFoundActionComplex.eventActionComplexHeader,
        eventActionComplexPrimaryCta: renderRichText(
          ticketsFoundActionComplex?.eventActionComplexPrimaryCta
            ?.microcopyValue?.raw ?? "",
          customRendererToRemovePTag
        ),
        eventActionComplexSecondaryCta: renderRichText(
          ticketsFoundActionComplex?.eventActionComplexSecondaryCta
            ?.microcopyValue?.raw ?? "",
          customRendererToRemovePTag
        ),
        eventActionComplexTertiaryCta: renderRichText(
          ticketsFoundActionComplex?.eventActionComplexTertiaryCta
            ?.microcopyValue?.raw ?? "",
          customRendererToRemovePTag
        )
      }
    }

    if (messageNoUpcomingOrders) {
      x.messageNoUpcomingOrders = renderRichText(
        messageNoUpcomingOrders.microcopyValue?.raw ?? "",
        customRendererToRemovePTag
      )
    }

    setContentfulContent(x)
  }, [
    noTicketsFoundActionComplex,
    ticketsFoundActionComplex,
    messageNoUpcomingOrders,
    orders
  ])

  useEffect(() => {
    if (orders && orders?.length > 0) {
      const filteredOrders: TrimmedSingleOrder[] = orders.map((x) => {
        const singleOrder = {} as TrimmedSingleOrder

        if (x.event?.logo?.url) {
          singleOrder.imageUrl = x.event.logo.url
        }
        if (x?.event?.name?.text) {
          singleOrder.title = x.event.name.text
        }
        if (x.event?.start?.utc) {
          singleOrder.displayDate = formatUTCDate(x.event.start.utc)
        }
        if (x.id) {
          singleOrder.ticketPageUrl = `${
            getBaseUrl() ? getBaseUrl() : process.env.GATSBY_PRODUCT_SITE_URL
          }/mytickets/${x.id}/`
        }

        return singleOrder
      })

      setTrimmedOrders(filteredOrders)
    } else {
      // if we have an empty array of tickets '[]' or if we for some reason receive a falsey value e.g. null, or undefined then we want to just set an empty array for orders
      setTrimmedOrders([])
    }
  }, [orders])

  if (process.env.GATSBY_DEBUG === "true") {
    console.log("🚀 ~ contentfulContent:", {
      contentfulContent
    })
  }

  if (!hasAttemptedLogin) {
    return null
  }

  if (!orders || orders?.length <= 0) {
    return (
      <div
        className={classNames(MyTicketsStyles.myticketsOuterContainer, {
          [`${MyTicketsStyles.myticketsColumnContainer}`]: columnLayout
            ? true
            : false
        })}
      >
        <div className={MyTicketsStyles.myticketsTitleWrapper}>
          <Typography
            className={MyTicketsStyles.myticketsTitle}
            variant="heading-sm"
            color="neutral-1000"
          >
            {contentfulContent?.noTicketsFoundContent?.eventActionComplexHeader
              ? contentfulContent?.noTicketsFoundContent
                  ?.eventActionComplexHeader
              : "My tickets"}
          </Typography>
        </div>

        <div className={MyTicketsStyles.noTicketsTopContainer}>
          <NoTicketsSvg />
          <Typography variant="body-lg">
            {contentfulContent?.messageNoUpcomingOrders
              ? contentfulContent.messageNoUpcomingOrders
              : "No upcoming orders"}
          </Typography>
          <OrdersBtn
            heapId="link.attendee-component.view-past-orders"
            className={MyTicketsStyles.noTicketsPastOrdersBtn}
          >
            {contentfulContent?.noTicketsFoundContent
              ?.eventActionComplexTertiaryCta
              ? contentfulContent.noTicketsFoundContent
                  .eventActionComplexTertiaryCta
              : "View past orders"}
          </OrdersBtn>
        </div>
        <div
          className={classNames(MyTicketsStyles.noTicketsBottomContainer, {
            [`${MyTicketsStyles.noTicketsFindMyTicketsColumnWrapper}`]:
              columnLayout ? true : false
          })}
        >
          <div
            className={MyTicketsStyles.buttonDiv}
            onClick={(e) =>
              handleMobileAppClick(
                `${
                  getBaseUrl()
                    ? getBaseUrl()
                    : process.env.GATSBY_PRODUCT_SITE_URL
                }/find-tickets/`,
                e
              )
            }
            onTouchStart={(e) => e.preventDefault()}
            onTouchEnd={(e) => {
              e.preventDefault()
              handleMobileAppClick(
                `${
                  getBaseUrl()
                    ? getBaseUrl()
                    : process.env.GATSBY_PRODUCT_SITE_URL
                }/find-tickets/`,
                e
              )
            }}
            data-heap-id="button.attendee-component.view-orders"
            style={{ cursor: "pointer" }}
          >
            <MarmaladeButton variant="primary">
              {contentfulContent?.noTicketsFoundContent
                ?.eventActionComplexPrimaryCta
                ? contentfulContent.noTicketsFoundContent
                    .eventActionComplexPrimaryCta
                : "Find my tickets"}
            </MarmaladeButton>
          </div>
          <div
            className={MyTicketsStyles.buttonDiv}
            onClick={(e) =>
              handleMobileAppClick(
                `${
                  getBaseUrl()
                    ? getBaseUrl()
                    : process.env.GATSBY_PRODUCT_SITE_URL
                }/d/`,
                e
              )
            }
            onTouchStart={(e) => e.preventDefault()}
            onTouchEnd={(e) => {
              e.preventDefault()
              handleMobileAppClick(
                `${
                  getBaseUrl()
                    ? getBaseUrl()
                    : process.env.GATSBY_PRODUCT_SITE_URL
                }/d/`,
                e
              )
            }}
            data-heap-id="button.attendee-component.city-browse"
            style={{ cursor: "pointer" }}
          >
            <MarmaladeButton variant="secondary">
              {contentfulContent?.noTicketsFoundContent
                ?.eventActionComplexSecondaryCta
                ? contentfulContent.noTicketsFoundContent
                    .eventActionComplexSecondaryCta
                : "Browse events"}
            </MarmaladeButton>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={classNames(MyTicketsStyles.myticketsOuterContainer, {
        [`${MyTicketsStyles.myticketsColumnContainer}`]: columnLayout
          ? true
          : false
      })}
    >
      <div className={MyTicketsStyles.myticketsTitleWrapper}>
        <Typography
          className={MyTicketsStyles.myticketsTitle}
          variant="heading-sm"
          color="neutral-1000"
        >
          {contentfulContent?.ticketsFoundActionComplex
            ?.eventActionComplexHeader
            ? contentfulContent?.ticketsFoundActionComplex
                ?.eventActionComplexHeader
            : "My tickets"}
        </Typography>

        <OrdersBtn
          className={MyTicketsStyles.myticketsAllOrdersBtnDesktop}
          style={{
            display: columnLayout ? "none" : "inline-block"
          }}
          heapId="button.attendee-component.view-orders-desktop"
        >
          {contentfulContent?.ticketsFoundActionComplex
            ?.eventActionComplexTertiaryCta
            ? contentfulContent?.ticketsFoundActionComplex
                ?.eventActionComplexTertiaryCta
            : "View all orders"}
        </OrdersBtn>
      </div>

      {activeOrder ? (
        <div className={MyTicketsStyles.myticketsPrimaryWrapper}>
          <div className={MyTicketsStyles.myticketsHeaderImageContainer}>
            <div
              onClick={(e) =>
                handleMobileAppClick(activeOrder.ticketPageUrl, e)
              }
              onTouchStart={(e) => e.preventDefault()}
              onTouchEnd={(e) => {
                e.preventDefault()
                handleMobileAppClick(activeOrder.ticketPageUrl, e)
              }}
              data-heap-id="img.attendee-component.image"
              className={MyTicketsStyles.aspectRatioBox}
              style={{ cursor: "pointer" }}
            >
              {activeOrder?.imageUrl ? (
                <img
                  className={MyTicketsStyles.myticketsHeaderImage}
                  src={activeOrder.imageUrl}
                  alt={`Image for event: ${activeOrder.title}`}
                />
              ) : (
                <img
                  className={MyTicketsStyles.myticketsHeaderImage}
                  src={placeholderSvgImg}
                  alt={`Image for event: ${activeOrder.title}`}
                />
              )}
            </div>
          </div>

          <div className={MyTicketsStyles.myticketsContentContainer}>
            <Typography
              variant="body-lg"
              className={MyTicketsStyles.myticketsEventName}
              color="neutral-1000"
            >
              {activeOrder.title}
            </Typography>
            <Typography variant="body-md-bold">
              {activeOrder.displayDate}
            </Typography>

            <div className={MyTicketsStyles.myticketsViewTicketsBtn}>
              <div
                onClick={(e) =>
                  handleMobileAppClick(activeOrder.ticketPageUrl, e)
                }
                onTouchStart={(e) => e.preventDefault()}
                onTouchEnd={(e) => {
                  e.preventDefault()
                  handleMobileAppClick(activeOrder.ticketPageUrl, e)
                }}
                data-heap-id="button.attendee-component.view-tickets"
                style={{ cursor: "pointer" }}
              >
                <MarmaladeButton variant="primary">
                  {contentfulContent?.ticketsFoundActionComplex
                    ?.eventActionComplexPrimaryCta
                    ? contentfulContent?.ticketsFoundActionComplex
                        ?.eventActionComplexPrimaryCta
                    : "View tickets"}
                </MarmaladeButton>
              </div>
            </div>
            <div
              className={MyTicketsStyles.myticketsAllOrdersBtnMobile}
              style={{
                display: columnLayout ? "inline-block" : ""
              }}
            >
              <OrdersBtn>
                {contentfulContent?.ticketsFoundActionComplex
                  ?.eventActionComplexTertiaryCta
                  ? contentfulContent?.ticketsFoundActionComplex
                      ?.eventActionComplexTertiaryCta
                  : "View all orders"}
              </OrdersBtn>
            </div>
          </div>
        </div>
      ) : (
        <>
          <LoaderSvg infiniteSpin={true} />
          <Typography variant="body-sm">Searching for your tickets</Typography>
        </>
      )}
    </div>
  )
}

const NoTicketsSvg = () => {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M23.6294 6.66699H30.3287V26.2318L10.5273 6.66699H16.9302C16.9302 6.66699 16.9302 10.1768 20.2798 10.1768C23.6294 10.1768 23.6294 6.66699 23.6294 6.66699ZM30.3285 29.6962L34.5139 33.8316L32.7687 35.5559L30.3285 33.1448H23.6293C23.6293 33.1448 23.6293 29.6333 20.2796 29.6333C16.93 29.6333 16.93 33.1448 16.93 33.1448H10.2307V13.2871L5.625 8.7347L7.37016 7.01204L10.2307 9.83846V9.82192L30.3285 29.6797V29.6962Z"
        fill="#3A3247"
      />
    </svg>
  )
}

const LoaderSvg: FC<{ infiniteSpin: boolean }> = ({ infiniteSpin }) => {
  return (
    <svg
      className={infiniteSpin ? MyTicketsStyles.loaderSvg : ""}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.7499 3.00022L21.0001 3V9.74989H14.2502L14.2499 7.50015H17.0191C15.7839 6.12099 13.993 5.25005 11.9999 5.25005C8.27847 5.25005 5.25006 8.27846 5.25006 11.9999C5.25006 15.7212 8.27847 18.7497 11.9999 18.7497C13.5174 18.7497 14.9146 18.24 16.043 17.3918L17.3941 19.1918C15.89 20.3224 14.0259 20.9996 11.9999 20.9996C7.02975 20.9996 3.00012 16.97 3.00012 11.9999C3.00012 7.02975 7.02975 3.00012 11.9999 3.00012C14.6904 3.00012 17.1023 4.18155 18.7499 6.05337V3.00022Z"
        fill="#3659E3"
      />
    </svg>
  )
}

/**
 *
 * @param utcString "2025-02-23T16:00:00Z"
 * @returns "Sun, Feb 23, 10:00 AM"
 */
const formatUTCDate = (utcString: string) => {
  const date = new Date(utcString)

  return (
    new Intl.DateTimeFormat("en-US", {
      weekday: "short", // Fri
      month: "short", // May
      day: "2-digit", // 26
      hour12: true // am/pm format
    }).format(date) +
    " • " +
    new Intl.DateTimeFormat("en-US", {
      hour: "numeric", // 10
      minute: "2-digit", // 30
      hour12: true // am/pm format
    }).format(date)
  )
}

const OrdersBtn: FC<{
  children: ReactNode
  heapId?: string
  className?: string
  style?: React.CSSProperties
}> = ({ className, children, heapId, style }) => {
  const myTicketsUrl = `${
    getBaseUrl() ? getBaseUrl() : process.env.GATSBY_PRODUCT_SITE_URL
  }/mytickets/`

  return (
    <div
      onClick={(e) => handleMobileAppClick(myTicketsUrl, e)}
      onTouchStart={(e) => e.preventDefault()}
      onTouchEnd={(e) => {
        e.preventDefault()
        handleMobileAppClick(myTicketsUrl, e)
      }}
      style={{ ...style, cursor: "pointer" }}
      data-heap-id={heapId ? heapId : ""}
    >
      <MarmaladeButton variant="ghost" className={classNames(className)}>
        {children}
      </MarmaladeButton>
    </div>
  )
}

const handleMobileAppClick = (
  url: string,
  event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
) => {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }

  let openedNewWindow = false

  try {
    const newWindow = window.open(url, "_blank")

    if (newWindow && !newWindow.closed) {
      openedNewWindow = true
    } else {
      window.location.href = url
    }
  } catch (err) {
    window.location.href = url
  }

  if (process.env.NODE_ENV === "development") {
    console.log("📱 handleMobileAppClick executed", {
      userAgent: window.navigator.userAgent,
      openedNewWindow
    })
  }
}
